// Один плоский список всего, что боец может держать: сток каждого класса + его скины.
// Так блок «Оружие» на вкладке достаёт ЛЮБОЙ скин, включая снайперский, — портрет держит
// винтовку, и выбор «скина того, что в руках» до AWP не дотянулся бы.
//
// Выбор ведётся ПО КАЖДОМУ классу отдельно: у автомата, снайперской и пулемёта свой скин,
// и все три настроены одновременно. Хранится это как `weaponKey → имя скина`, ровно так же,
// как на сервере.

import type { WeaponClass } from '@cs-shot/battle-sim';
import type { RushMidWeaponSkinOption } from '../../../../services/generated/Api';

export type { RushMidWeaponSkinOption };

export interface RailEntry {
  weapon: WeaponClass;
  /** ключ спрайта: rifle → ak на T, m4 на CT */
  weaponKey: string;
  /** null = сток этого класса */
  skin: RushMidWeaponSkinOption | null;
  /** подпись под превью */
  title: string;
  classLabel: string;
}

export const WEAPON_CLASSES: { weapon: WeaponClass; label: string }[] = [
  { weapon: 'rifle', label: 'Автомат' },
  { weapon: 'awp', label: 'Снайперская' },
  { weapon: 'lmg', label: 'Пулемёт' },
];

export const keyOf = (side: 't' | 'ct', weapon: WeaponClass): string =>
  weapon === 'rifle' ? (side === 'ct' ? 'm4' : 'ak') : weapon;

/** Все записи для стороны: по каждому классу сток, затем его скины из каталога. */
export function railFor(side: 't' | 'ct', catalog: RushMidWeaponSkinOption[]): RailEntry[] {
  return WEAPON_CLASSES.flatMap(({ weapon, label }) => {
    const weaponKey = keyOf(side, weapon);
    const stock: RailEntry = { weapon, weaponKey, skin: null, title: 'Сток', classLabel: label };
    const skins = catalog
      .filter((s) => s.weaponKey === weaponKey)
      .map((s) => ({ weapon, weaponKey, skin: s, title: s.title, classLabel: label }));
    return [stock, ...skins];
  });
}

/** Надета ли ИМЕННО эта запись. Сток своего класса считается надетым, когда для этого ключа
 *  ничего не выбрано, — иначе у класса без скина не было бы подсвеченной плитки. */
export const isWorn = (entry: RailEntry, selection: Record<string, string>): boolean =>
  entry.skin ? selection[entry.weaponKey] === entry.skin.name : !selection[entry.weaponKey];

/** Что надето на одном классе — для портрета, который держит один ствол за раз. */
export function wornFor(rail: RailEntry[], selection: Record<string, string>, weapon: WeaponClass): RailEntry {
  const ofClass = rail.filter((e) => e.weapon === weapon);
  return ofClass.find((e) => isWorn(e, selection)) ?? ofClass[0] ?? rail[0];
}
